import { useState } from "react";
import type { SlideProps } from "@/engine/app/offsite-config";
import { api } from "@/api/client";
import { useStore } from "@/state/store";

/**
 * Übergang Themenfindung → Prompten (Tag 2, ca. 9:05; seit der Umplanung
 * 24.09. mittags findet die Themenfindung am selben Morgen statt). Hält fest,
 * was steht, und nennt die Zeiten des restlichen Vormittags. Die Position im
 * Presenter-Knopf „Arbeitsphase starten" öffnet die Handys mit 30-Min-Uhr —
 * bewusst ein Knopf, nicht „Weiter" (Klicker senden mehrere Tasten).
 */

const HEUTE = [
  { zeit: "9:05", titel: "Prompten", text: "Steckbrief fertig ausfüllen — im Team, einer tippt." },
  { zeit: "9:35", titel: "SVO OS", text: "Was mit KI gebaute Software heute schon kann." },
  { zeit: "9:50", titel: "Bewertung", text: "Die KI sagt euch, wie klar eure Anforderung ist." },
  { zeit: "10:35", titel: "Ergebnisse", text: "Eure Prototypen auf der Leinwand — Team für Team." },
];

const MINUTEN = 30;

export function Slide13cTag2(_: SlideProps) {
  const { state, dispatch } = useStore();
  const istPresenter = state.auth.role === "presenter";
  const phase = state.workshop.phase.phase;
  const [startet, setStartet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const starten = async () => {
    setStartet(true);
    setFehler(null);
    try {
      const p = await api.phase.set(state.auth.token, "working", MINUTEN * 60);
      dispatch({ type: "phase/set", phase: p });
    } catch {
      setFehler("Start fehlgeschlagen — nochmal klicken");
    } finally {
      setStartet(false);
    }
  };

  return (
    <div className="deck-root dark" style={{ background: "var(--svo-dunkelblau)" }}>
      <div className="deck-bg" aria-hidden="true" />
      <div
        className="float"
        aria-hidden="true"
        style={{
          width: 460,
          height: 460,
          top: "-140px",
          right: "-80px",
          background: "var(--svo-blau)",
          opacity: 0.3,
        }}
      />
      <div
        className="float"
        aria-hidden="true"
        style={{
          width: 320,
          height: 320,
          bottom: "-110px",
          left: "8%",
          background: "var(--svo-gruen)",
          opacity: 0.14,
          animationDelay: "4s",
        }}
      />

      <header className="deck-head">
        <div className="deck-eyebrow rise rise-1">Jetzt wird es konkret</div>
        <h1
          className="deck-title rise rise-2"
          style={{
            fontSize: "clamp(3rem, 7vw, 5.5rem)",
            fontWeight: 200,
            color: "var(--svo-blau)",
            lineHeight: 1,
            letterSpacing: "-0.03em",
          }}
        >
          Thema steht.
        </h1>
        <p className="deck-sub rise rise-3">
          Jetzt sagt ihr der KI genau, was sie bauen soll — und seht bis
          Mittag, was dabei herauskommt.
        </p>
      </header>

      <div
        className="rise rise-4"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          // Karten wachsen mit ihrem Text, statt die Resthöhe auszufüllen.
          alignItems: "start",
          gap: 18,
          width: "100%",
          maxWidth: 1400,
          margin: "0 auto",
        }}
      >
        {HEUTE.map((h) => (
          <div
            key={h.zeit}
            style={{
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.16)",
              borderRadius: 14,
              padding: "18px 20px",
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "clamp(0.85rem, 1.15vw, 1.05rem)",
                fontVariantNumeric: "tabular-nums",
                color: "var(--svo-blau)",
              }}
            >
              {h.zeit}
            </span>
            <span
              style={{
                fontSize: "clamp(1.05rem, 1.6vw, 1.45rem)",
                fontWeight: 700,
              }}
            >
              {h.titel}
            </span>
            <span
              style={{
                fontSize: "clamp(0.8rem, 1.1vw, 1rem)",
                opacity: 0.75,
                lineHeight: 1.35,
              }}
            >
              {h.text}
            </span>
          </div>
        ))}
      </div>

      {istPresenter && (phase === "waiting" || phase === "einfuehrung") && (
        <div className="rise rise-4" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
          <button
            type="button"
            data-testid="arbeitsphase-starten"
            onClick={() => void starten()}
            disabled={startet}
            style={{
              background: "var(--svo-blau)",
              color: "#fff",
              border: "none",
              borderRadius: 12,
              padding: "16px 36px",
              fontSize: "clamp(1.1rem, 1.6vw, 1.5rem)",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {startet ? "…" : `▶ Arbeitsphase starten · ${MINUTEN} Min`}
          </button>
          {fehler && <span style={{ color: "#ffb4a8", fontSize: 14 }}>{fehler}</span>}
        </div>
      )}

      <footer className="deck-foot">
        <span>
          Euer Thema steht schon im Steckbrief — gleich geht es am selben
          Handy weiter.
        </span>
      </footer>
    </div>
  );
}
